<template>
  <lf-dropdown
    ref="dropdown"
    class="w-full"
    :value="value || undefined"
    :placeholder="placeholder"
    :options="dropdownOptions"
    :is-loading="isLoadingMore"
    :name="name"
    :full-width="fullWidth"
    :show-clear-filter="showClearFilter"
    :as-badge="asBadge"
    :teleport-options="teleportOptions"
    :search-placeholder="searchTerm"
    :warning-as-error="warningAsError"
    :drop-up="dropUp"
    :disabled="disabled"
    :sort="sort"
    search-enabled
    :menu-width="menuWidth"
    :show-warning-icon="showWarningIcon"
    @change="handleOptionChange"
    @toggle="handleDropdownToggled"
    @search:change="handleSearchChange"
  >
    <template v-if="$slots['toggle-button']" #toggle-button="scope">
      <slot name="toggle-button" v-bind="scope" />
    </template>
    <template #label="{ option, isSelected, optionId }">
      <slot
        name="option"
        :option="option"
        :options="paginatedData"
        :is-selected="isSelected"
        :option-id="optionId"
      />
    </template>
    <template v-if="hasNextPage" #action>
      <a
        class="transform cursor-pointer text-sm font-medium text-primary active:scale-95"
        @click.stop="loadMore()"
      >
        {{ $t("COMMON.LOAD_MORE") }}
      </a>
    </template>
  </lf-dropdown>
</template>

<script lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";

import get from "lodash/get";

import type { IPaginatedResponse } from "@/models/common";
import type { QueryKey } from "@tanstack/vue-query";

export const NONE_OPTION_ID = "0";

export interface LfInfiniteQueryOptions {
  queryKey: QueryKey;
  queryFn: (params: {
    page: number;
    search?: string;
  }) => Promise<IPaginatedResponse<unknown>>;
  staleTime?: number;
  gcTime?: number;
}

type LfDropdownPropTypes = InstanceType<typeof LfDropdown>["$props"];

interface Props {
  teleportOptions?: LfDropdownPropTypes["teleportOptions"];
  asBadge?: LfDropdownPropTypes["asBadge"];
  fullWidth?: LfDropdownPropTypes["fullWidth"];
  showClearFilter?: LfDropdownPropTypes["showClearFilter"];
  warningAsError?: LfDropdownPropTypes["warningAsError"];
  value?: string | number | boolean;
  placeholder?: string;
  accessor: string;
  name?: string;
  searchTerm?: string;
  includeNone?: boolean;
  noneOptionLabel?: string;
  identifier?: string;
  showWarningIcon?: boolean;
  dropUp?: boolean;
  disabled?: boolean;
  sort?: boolean;
  menuWidth?: string;
  queryOptions: LfInfiniteQueryOptions;
}
</script>

<script setup lang="ts">
import { useInfiniteQuery, useQueryClient } from "@tanstack/vue-query";

import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

const emit = defineEmits<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (e: "change", val: any): void;
  (e: "loading", loading: boolean): void;
}>();

const props = withDefaults(defineProps<Props>(), {
  value: "",
  placeholder: "",
  name: "",
  searchTerm: "",
  fullWidth: false,
  showClearFilter: false,
  asBadge: false,
  includeNone: false,
  identifier: "id",
  warningAsError: false,
  showWarningIcon: false,
  dropUp: false,
  disabled: false,
  sort: false,
});

const queryClient = useQueryClient();
const dropdown = ref<InstanceType<typeof LfDropdown> | null>(null);
const search = ref("");
const debouncedSearch = ref("");
const activated = ref(false);
let debounceTimer: ReturnType<typeof setTimeout> | undefined;

const queryKey = computed(() => [
  ...props.queryOptions.queryKey,
  { search: debouncedSearch.value },
]);

const infiniteQuery = useInfiniteQuery({
  queryKey,
  queryFn: async ({ pageParam }) => {
    return props.queryOptions.queryFn({
      page: pageParam,
      ...(debouncedSearch.value && { search: debouncedSearch.value }),
    });
  },
  getNextPageParam: (lastPage) => {
    const { current_page, last_page } = lastPage.meta;
    return current_page < last_page ? current_page + 1 : undefined;
  },
  initialPageParam: 1,
  enabled: activated,
  staleTime: () => props.queryOptions.staleTime ?? 5 * 60 * 1000,
  gcTime: props.queryOptions.gcTime ?? 10 * 60 * 1000,
});

const paginatedData = computed(
  () => infiniteQuery.data.value?.pages.flatMap((p) => p.data) ?? []
);

const isLoadingMore = computed(
  () => infiniteQuery.isFetchingNextPage.value || infiniteQuery.isLoading.value
);

const hasNextPage = computed(() => !!infiniteQuery.hasNextPage.value);

const dropdownOptions = computed(() => {
  let options = paginatedData.value.reduce<Record<string, string>>(
    (acc, item) => {
      const typedItem = item as Record<string, unknown>;
      const id = typedItem[props.identifier] as string;
      const value = get(typedItem, props.accessor) as string;
      acc[id] = value;
      return acc;
    },
    {}
  );

  if (props.includeNone) {
    options = { [NONE_OPTION_ID]: props.noneOptionLabel ?? "None", ...options };
  }

  return options;
});

const updateDebouncedSearch = (value: string) => {
  clearTimeout(debounceTimer);
  if (!value) {
    debouncedSearch.value = "";
    return;
  }
  debounceTimer = setTimeout(() => {
    debouncedSearch.value = value;
  }, 300);
};

const handleSearchChange = (value: string) => {
  if (value === search.value) {
    return;
  }
  search.value = value;
  if (dropdown.value) {
    dropdown.value.search = value;
  }
  updateDebouncedSearch(value);
};

const handleOptionChange = (val: string | number) => {
  if (props.includeNone && val.toString() === NONE_OPTION_ID) {
    emit("change", undefined);
    handleSearchChange("");
    return;
  }
  const selectedValue = paginatedData.value.find(
    (item) =>
      (item as Record<string, unknown>)[props.identifier]?.toString() ===
      val.toString()
  );
  emit("change", selectedValue);
  handleSearchChange("");
};

const handleDropdownToggled = (value: boolean) => {
  if (!value) return;
  activated.value = true;
};

const loadMore = async () => {
  await infiniteQuery.fetchNextPage();
};

const resetOptions = () => {
  queryClient.resetQueries({ queryKey: queryKey.value });
};

onBeforeUnmount(() => {
  clearTimeout(debounceTimer);
});

watch(
  () => infiniteQuery.isFetching.value,
  (fetching) => {
    emit("loading", fetching);
  }
);

defineExpose({
  resetOptions,
  loadMore,
  paginatedData,
  handleSearchChange,
  resetAll: () => {
    resetOptions();
    handleSearchChange("");
  },
});
</script>
